import type { CSSProperties } from 'react'

/**
 * Welcher Ausschnitt eines Bildes zu sehen ist (#51).
 *
 * Drei Zahlen: der **Blickpunkt** im Bild (`x`, `y` in Prozent) und die
 * **Größe** (`zoom` in Prozent von „füllt die Fläche"). `50/50/100` heißt
 * mittig und füllend — genau der Anblick, den es vor #51 gab.
 *
 * **Gemerkt statt zugeschnitten.** Ein beim Hochladen fest beschnittenes Bild
 * wäre nicht mehr zu ändern, verlöre Bildpunkte, und es passte immer nur zu
 * **einer** Flächenform — der Chat ist am iPhone hochkant und am iPad fast
 * quadratisch.
 */
export interface Ausschnitt {
  x: number
  y: number
  zoom: number
}

/** Mittig und füllend — was ohne Angabe gilt. */
export const OHNE: Ausschnitt = { x: 50, y: 50, zoom: 100 }

/** Was der Server liefert, auf drei Zahlen gebracht. */
export function ausschnittAus(roh: Partial<Ausschnitt> | null | undefined): Ausschnitt {
  return {
    x: roh?.x ?? OHNE.x,
    y: roh?.y ?? OHNE.y,
    zoom: roh?.zoom ?? OHNE.zoom,
  }
}

/**
 * Ein `<img>`, das seine Fläche **immer füllt** — Symbolbild und Buchdeckel.
 *
 * `object-fit: cover` füllt, `object-position` wählt den sichtbaren Ausschnitt,
 * und `transform: scale()` vergrößert **um denselben Punkt** (`transform-origin`).
 * Weil `cover` schon füllt, kann dabei nie ein Rand entstehen; deshalb braucht
 * es hier weder Grenzen noch eine Messung.
 *
 * Der umgebende Kasten muss `overflow: hidden` tragen — sonst malt ein
 * vergrößertes Bild darüber hinaus.
 */
export function bildStil(a: Ausschnitt): CSSProperties {
  const punkt = `${a.x}% ${a.y}%`
  return {
    objectFit: 'cover',
    objectPosition: punkt,
    ...(a.zoom === 100
      ? {}
      : { transform: `scale(${a.zoom / 100})`, transformOrigin: punkt }),
  }
}

/**
 * Eine Hintergrundfläche, die **kleiner als ihre Fläche sein darf**.
 *
 * **Und genau deshalb reicht `cover` hier nicht.** Eine verkleinerte
 * `cover`-Ebene zeigt denselben Beschnitt, nur kleiner — niemals das ganze
 * Bild. Gerechnet wird deshalb in Bildpunkten:
 *
 * ```
 * fuellend = max(Fläche.b / Bild.b, Fläche.h / Bild.h)   // was „cover" täte
 * groesse  = Bild × fuellend × zoom
 * ```
 *
 * Bei `zoom = 100` ist das Punkt für Punkt der bisherige Anblick; darunter
 * schrumpft es, bis das ganze Motiv auf der Hintergrundfarbe steht.
 *
 * Ohne bekannte Bildgröße (`0`) oder ohne gemessene Fläche bleibt es beim
 * füllenden Zuschnitt — ein Hintergrund, der auf eine Messung wartet, wäre
 * schlechter als einer, der wie bisher aussieht.
 */
export function grundStil(
  a: Ausschnitt,
  flaeche: { breite: number; hoehe: number },
  bild: { breite: number; hoehe: number },
): CSSProperties {
  const punkt = `${a.x}% ${a.y}%`
  const messbar =
    flaeche.breite > 0 && flaeche.hoehe > 0 && bild.breite > 0 && bild.hoehe > 0
  if (!messbar) {
    return { backgroundSize: 'cover', backgroundPosition: punkt }
  }

  const fuellend = Math.max(flaeche.breite / bild.breite, flaeche.hoehe / bild.hoehe)
  const faktor = (fuellend * a.zoom) / 100
  return {
    backgroundSize: `${Math.round(bild.breite * faktor)}px ${Math.round(bild.hoehe * faktor)}px`,
    backgroundPosition: punkt,
    // Sonst kachelt ein verkleinertes Bild die Fläche voll, statt die Farbe
    // stehen zu lassen.
    backgroundRepeat: 'no-repeat',
  }
}
